import { useState, useMemo, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Container,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { useAppDispatch } from 'src/store/hooks'
import { useDashboardData } from '../dashboard/hooks/use-dashboard-data'
import UserDataGrid from './components/user-data-grid'
import UserManagement from './components/user-management'
import RosterStatistics from './components/roster-statistics'
import { useRosterStats } from './hooks/use-roster-stats'
import { activeLinkedServiceChanged } from '../manage.slice'
import { useNotifications } from '../hooks/use-notifications'

interface RosterFilters {
  linkedServiceID: string
  searchQuery: string
}

export default function RosterV4() {
  let dispatch = useAppDispatch()
  let location = useLocation()
  let navigate = useNavigate()
  let { showSuccess, showError, NotificationComponent } = useNotifications()

  let initialLinkedServiceID =
    (location.state as { linkedServiceID?: string } | null)?.linkedServiceID ||
    ''

  let [filters, setFilters] = useState<RosterFilters>({
    linkedServiceID: initialLinkedServiceID,
    searchQuery: '',
  })

  let {
    linkedServices,
    lightweightUsers,
    isLoading: hookIsLoading,
  } = useDashboardData(filters.linkedServiceID, (serviceID: string) => {
    setFilters((prev) => ({ ...prev, linkedServiceID: serviceID }))
    dispatch(activeLinkedServiceChanged(serviceID))
  })

  useEffect(() => {
    let state = location.state as { linkedServiceID?: string } | null
    if (state?.linkedServiceID) {
      dispatch(activeLinkedServiceChanged(state.linkedServiceID))
      // Clear the state after using it
      navigate(location.pathname, {
        replace: true,
        state: { ...state, linkedServiceID: undefined },
      })
    }
  }, [])

  let isLoading: boolean = !!hookIsLoading

  let rosterStats = useRosterStats({
    users: lightweightUsers || [],
    isLoading,
  })

  let updateFilter = <K extends keyof RosterFilters>(
    key: K,
    value: RosterFilters[K],
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }))

    if (key === 'linkedServiceID') {
      dispatch(activeLinkedServiceChanged(value as string))
    }
  }

  let filteredUsers = useMemo(() => {
    let users = lightweightUsers || []

    return users.filter((user) => {
      if (!user || !user.id) return false

      if (filters.searchQuery) {
        let query = filters.searchQuery.toLowerCase()
        let firstName = user.firstName || ''
        let lastName = user.lastName || ''
        let email = user.primaryEmail || ''
        let matchesName = `${firstName} ${lastName}`
          .toLowerCase()
          .includes(query)
        let matchesEmail = email.toLowerCase().includes(query)
        if (!matchesName && !matchesEmail) return false
      }

      return true
    })
  }, [lightweightUsers, filters.searchQuery])

  let selectedLinkedServiceUserCap = linkedServices.find(
    (service) => service.id === filters.linkedServiceID,
  )?.userCap

  return (
    <Container maxWidth="xl" className="py-8">
      <Box className="mb-8">
        <Typography variant="h4" component="h1">
          Roster
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage current and invited users ({lightweightUsers.length} total)
        </Typography>
      </Box>
      <RosterStatistics
        totalUsers={rosterStats.totalUsers}
        usersNotLoggedIn={rosterStats.usersNotLoggedIn}
        isLoading={rosterStats.isLoading}
      />
      <FormControl fullWidth className="mb-6">
        <InputLabel>Select Linked Service</InputLabel>
        <Select
          sx={{ backgroundColor: 'background.paperDark' }}
          value={filters.linkedServiceID}
          onChange={(e) => updateFilter('linkedServiceID', e.target.value)}
          label="Select Linked Service"
        >
          {linkedServices.map((service) => (
            <MenuItem key={service.id} value={service.id}>
              {service.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box className="flex flex-col gap-6">
        <UserManagement
          userCount={filteredUsers.length}
          userCap={selectedLinkedServiceUserCap}
          isLoading={isLoading}
          linkedServiceID={filters.linkedServiceID}
          onSuccess={(message) => showSuccess(message)}
          onError={(message) => showError(message)}
        />

        <UserDataGrid
          users={filteredUsers}
          isLoading={isLoading}
          linkedServiceId={filters.linkedServiceID}
          searchQuery={filters.searchQuery}
          onSearchChange={(value) => updateFilter('searchQuery', value)}
        />
      </Box>
      {NotificationComponent}
    </Container>
  )
}
